import { FieldShell } from '../FieldShell'
import type { FieldComponentProps } from '../rendererContext'
import type { NumberConfig } from '../types'
import { controlClass, useControlMeta } from './useControlMeta'

/**
 * Parse the input string into the value stored on change: empty → undefined (so
 * `required` sees it as blank); a parseable number → number; otherwise the raw
 * string preserved so the validator can flag `not_a_number`.
 */
function parseNumberInput(raw: string): unknown {
  if (raw === '') return undefined
  const n = Number(raw)
  return Number.isNaN(n) ? raw : n
}

export function NumberField({ field, path, value, errors }: FieldComponentProps) {
  const meta = useControlMeta(field, path, errors)
  const config = field.config as NumberConfig | undefined

  if (field.display_only) {
    return (
      <FieldShell field={field} path={path} errors={errors}>
        <div id={meta.id} className="form-control-plaintext">
          {value == null ? '' : String(value)}
        </div>
      </FieldShell>
    )
  }

  return (
    <FieldShell field={field} path={path} errors={errors}>
      <input
        id={meta.id}
        type="number"
        className={controlClass('form-control', meta.classNames.control, meta.hasErrors)}
        value={value == null ? '' : String(value)}
        min={config?.min}
        max={config?.max}
        step={config?.step ?? (config?.integer ? 1 : undefined)}
        placeholder={config?.placeholder}
        disabled={meta.disabled}
        aria-invalid={meta.ariaInvalid}
        aria-required={meta.ariaRequired}
        aria-describedby={meta.describedBy}
        onChange={(event) => meta.setFieldValue(path, parseNumberInput(event.target.value))}
        onBlur={() => meta.notifyBlur(path)}
      />
    </FieldShell>
  )
}
